import {
  BillingHeader,
  BillingEmptyRow,
  BillingPagination
} from './components/OrganizationBillingLayout';
import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table
} from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faClock,
  faFilter,
  faTimes,
  faFileInvoiceDollar,
  faUser,
  faCog
} from '@fortawesome/free-solid-svg-icons';
import './AdminBillingAudit.css';
import {
  useGetOrganizationBillingAuditsQuery,
  useGetOrganizationBillingAuditLabelsQuery
} from 'rtk/org-admin/organizationBilling.slice';
import {
  billingDayStartUtc,
  formatBillingDate
} from 'util/organizationBillingFormat';
import {
  billingActivityLabel,
  billingAuditKey,
  billingAuditActor
} from 'util/organizationBillingAuditDisplay';

const EVENT_TYPES = [
  'SponsorshipStarted',
  'SponsorshipEndRequested',
  'PaymentMethodAdded',
  'PaymentMethodRemoved',
  'PaymentMethodDefaultChanged',
  'PaymentMethodRoleChanged',
  'PaymentAttemptCreated',
  'PaymentSucceeded',
  'InvoicePaid',
  'PaymentFailed',
  'ProfileRestrictedPaymentFailed',
  'ProfileAccessRestored',
  'HostedDataDeleted',
  'BillingCycleRequiresReview'
];
const ENTITY_TYPES = [
  'Sponsorship',
  'HostedAccount',
  'Invoice',
  'PaymentMethod',
  'OrganizationPayment',
  'BillingCycle',
  'HostedDeletion',
  'BillingIntegrity'
];

const eventStyle = type => {
  if (/Failed|Restricted|RequiresReview|Inconsistent/.test(type))
    return { tone: 'warning', icon: faExclamationTriangle };
  if (/Succeeded|Paid|Activated|Restored|Completed/.test(type))
    return { tone: 'success', icon: faCheckCircle };
  if (/Requested|AttemptCreated|Scheduled/.test(type))
    return { tone: 'neutral', icon: faClock };
  return { tone: 'primary', icon: faFileInvoiceDollar };
};

const AdminBillingAudit = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({});
  const query = {
    indexstart: page * pageSize,
    maxresults: pageSize,
    ...Object.fromEntries(Object.entries(filters).filter(([, value]) => value)),
    // Date filters hold the picked day; send it as that day's start in the
    // user's timezone so it matches the dates shown in the table.
    ...(filters.fromutc
      ? { fromutc: billingDayStartUtc(filters.fromutc) }
      : {}),
    ...(filters.toutc ? { toutc: billingDayStartUtc(filters.toutc) } : {})
  };
  const result = useGetOrganizationBillingAuditsQuery(query);
  const data = result.data?.Data ?? result.data?.data ?? result.data;
  const events = data?.Objects ?? [];
  const references = events.map(
    ({ EntityType, EntityGUID, ActorUserMasterGUID }) => ({
      EntityType,
      EntityGUID,
      ActorUserMasterGUID
    })
  );
  const labels = useGetOrganizationBillingAuditLabelsQuery(references, {
    skip: !events.length
  });
  const loading = result.isLoading || result.isFetching || labels.isFetching;
  const change = (key, value) => {
    setFilters(current => ({ ...current, [key]: value }));
    setPage(0);
  };
  const refresh = () => {
    result.refetch();
    if (events.length) labels.refetch();
  };
  return (
    <Card className="billing-audit">
      <BillingHeader
        title="Billing audit history"
        subtitle="Review billing activity and filter events for your organization."
        onRefresh={refresh}
        loading={loading}
      />
      <Card.Body className="px-4">
        <div className="billing-audit-filters mb-4">
          <div className="d-flex align-items-center justify-content-between mb-3">
            <span className="fw-semi-bold text-900 fs--1">
              <FontAwesomeIcon icon={faFilter} className="me-2 text-600" />
              Filter activity
            </span>
            <Button
              variant="outline-secondary"
              size="sm"
              className="billing-audit-clear"
              disabled={!Object.values(filters).some(Boolean)}
              onClick={() => {
                setFilters({});
                setPage(0);
              }}
            >
              <FontAwesomeIcon icon={faTimes} className="me-1" />
              Clear filters
            </Button>
          </div>
          <Row className="g-3">
            {[
              [
                'eventtype',
                'Event type',
                [
                  ...new Set([
                    ...EVENT_TYPES,
                    ...events.map(event => event.EventType),
                    filters.eventtype
                  ])
                ]
              ],
              [
                'entitytype',
                'Related item',
                [
                  ...new Set([
                    ...ENTITY_TYPES,
                    ...events.map(event => event.EntityType),
                    filters.entitytype
                  ])
                ]
              ]
            ].map(([key, label, options]) => (
              <Col key={key} xs={12} md={6} xl={3}>
                <Form.Label htmlFor={`audit-${key}`}>{label}</Form.Label>
                <Form.Select
                  id={`audit-${key}`}
                  value={filters[key] ?? ''}
                  onChange={event => change(key, event.target.value)}
                >
                  <option value="">
                    All {key === 'eventtype' ? 'events' : 'items'}
                  </option>
                  {options.filter(Boolean).map(value => (
                    <option key={value} value={value}>
                      {billingActivityLabel(value)}
                    </option>
                  ))}
                </Form.Select>
              </Col>
            ))}
            {[
              ['fromutc', 'From'],
              ['toutc', 'Before']
            ].map(([key, label]) => (
              <Col key={key} xs={12} md={6} xl={3}>
                <Form.Label htmlFor={`audit-${key}`}>{label}</Form.Label>
                <Form.Control
                  id={`audit-${key}`}
                  type="date"
                  value={filters[key] ?? ''}
                  onChange={event => change(key, event.target.value)}
                />
              </Col>
            ))}
          </Row>
        </div>
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
          <h5 className="mb-0">Activity history</h5>
          <span className="text-600 fs--1">
            {data?.TotalRecords != null
              ? `${data.TotalRecords} ${
                  data.TotalRecords === 1 ? 'event' : 'events'
                }`
              : 'Organization billing events'}
          </span>
        </div>
        {result.isLoading && (
          <div className="text-center py-4" role="status">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading billing activity
          </div>
        )}
        {result.error && (
          <Alert variant="danger">
            Unable to load billing audit history. Check your permissions and
            filter dates, then retry.
          </Alert>
        )}
        <BillingPagination
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={size => {
            setPageSize(size);
            setPage(0);
          }}
          data={data}
          onRefresh={refresh}
          loading={loading}
          error={result.error}
        />
        <Table
          responsive
          hover
          className={`billing-audit-table fs--1 mb-0 align-middle${
            loading ? ' refreshing-skeleton' : ''
          }`}
          aria-busy={Boolean(loading)}
        >
          <thead className="bg-200 text-900 text-nowrap align-middle">
            <tr>
              <th scope="col">Activity</th>
              <th scope="col">Related item</th>
              <th scope="col">Performed by</th>
              <th scope="col">Date & time</th>
            </tr>
          </thead>
          <tbody>
            {loading && !events.length && (
              <tr>
                <td colSpan={4} className="py-5">
                  <span className="visually-hidden">
                    Loading billing activity
                  </span>
                </td>
              </tr>
            )}
            {!loading && !result.error && !data?.Objects?.length && (
              <BillingEmptyRow colSpan={4}>
                No billing events match these filters.
              </BillingEmptyRow>
            )}
            {data?.Objects?.map(event => {
              const style = eventStyle(event.EventType);
              const entityLabel =
                labels.data?.entities?.[billingAuditKey(event)];
              return (
                <tr key={event.GUID}>
                  <td data-label="Activity" className="billing-audit-activity">
                    <div className="d-flex align-items-center gap-3">
                      <span
                        className={`billing-audit-icon billing-audit-icon-${style.tone}`}
                      >
                        <FontAwesomeIcon icon={style.icon} />
                      </span>
                      <span className="fw-semi-bold text-900">
                        {billingActivityLabel(event.EventType)}
                      </span>
                    </div>
                  </td>
                  <td data-label="Related item" className="text-break">
                    <div
                      className={
                        entityLabel ? 'fw-semi-bold text-900' : 'text-900'
                      }
                    >
                      {entityLabel || billingActivityLabel(event.EntityType)}
                    </div>
                    {entityLabel && (
                      <div className="text-600 fs--1 mt-1">
                        {billingActivityLabel(event.EntityType)}
                      </div>
                    )}
                  </td>
                  <td data-label="Performed by" className="text-break">
                    <div className="d-flex align-items-start gap-2">
                      <FontAwesomeIcon
                        icon={event.ActorUserMasterGUID ? faUser : faCog}
                        className="text-500 mt-1 flex-shrink-0"
                      />
                      <div>
                        <div className="text-900">
                          {billingAuditActor(event, labels.data)}
                        </div>
                        <div className="text-600 fs--1 mt-1">
                          {event.ActorUserMasterGUID
                            ? 'Organization member'
                            : 'Automatic activity'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td data-label="Date & time" className="billing-audit-date">
                    <div>
                      <div className="text-900">
                        {formatBillingDate(event.OccurredUtc, 'MMM D, YYYY')}
                      </div>
                      <div className="text-600 mt-1">
                        {formatBillingDate(event.OccurredUtc, 'h:mm A')}
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card.Body>
    </Card>
  );
};

export default AdminBillingAudit;
